import { Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { TextareaModule } from 'primeng/textarea';
import { TooltipModule } from 'primeng/tooltip';
import { finalize } from 'rxjs';

import { AgentSessionService } from 'src/app/@core/services/ai/agent-session.service';
import { AgentTurnFeedback } from 'src/app/@shared/models/ai/agent-turn.model';

/** Avis déposé, remplacé ou retiré : ce que le serveur a enregistré pour le tour. */
export interface AssistantFeedbackChange {
    feedback: AgentTurnFeedback | null;
    feedbackComment: string;
}

/** Longueur maximale du commentaire acceptée par le serveur. */
const COMMENT_MAX_LENGTH = 2000;

/**
 * Avis de l'utilisateur sur la réponse d'un tour (#1284) : un pouce levé ou baissé.
 *
 * - Pouce levé : enregistré au clic ; un second clic retire l'avis.
 * - Pouce baissé : ouvre un champ de commentaire **obligatoire** — l'avis n'est enregistré qu'à
 *   l'envoi du texte. Sur un avis négatif déjà donné, le clic rouvre le commentaire pour le
 *   modifier, et « Retirer mon avis » retire l'avis.
 * - Lecture seule (supervision qui lit la conversation d'un autre) : l'avis et son commentaire
 *   s'affichent, rien ne se modifie.
 *
 * L'avis affiché vient de l'hôte (`feedback`, `feedbackComment`) : le composant ne le retient pas,
 * il émet `changed` et l'hôte le reporte dans le fil.
 */
@Component({
    selector: 'app-assistant-feedback',
    standalone: true,
    imports: [
        FormsModule,
        ButtonModule,
        TextareaModule,
        TooltipModule,
    ],
    templateUrl: './assistant-feedback.component.html',
})
export class AssistantFeedbackComponent {

    /*----------------------------------------------------------------------------------------
     * Gestion de la vue
     ----------------------------------------------------------------------------------------*/

    readonly commentMaxLength: number = COMMENT_MAX_LENGTH;

    private _sessionService = inject(AgentSessionService);

    /*----------------------------------------------------------------------------------------
     * Données
     ----------------------------------------------------------------------------------------*/

    @Input() sessionId: string = '';
    @Input() turnIndex: number = 0;
    @Input() feedback: AgentTurnFeedback | null = null;
    @Input() feedbackComment: string = '';
    @Input() readonly: boolean = false;

    @Output() changed = new EventEmitter<AssistantFeedbackChange>();

    draft: string = '';
    editing: boolean = false;
    saving: boolean = false;

    /*----------------------------------------------------------------------------------------
     * Accesseurs
     ----------------------------------------------------------------------------------------*/

    /** Le pouce baissé reste allumé pendant la saisie de son commentaire. */
    public get thumbDownActive(): boolean {
        return this.editing || this.feedback === 'negative';
    }

    public get thumbUpActive(): boolean {
        return !this.editing && this.feedback === 'positive';
    }

    /*----------------------------------------------------------------------------------------
     * Méthodes publiques
     ----------------------------------------------------------------------------------------*/

    public onClickCancel(): void {
        this._closeEditor();
    }

    public onClickRemove(): void {
        this._save(null, '');
    }

    public onClickSend(): void {
        this._save('negative', this.draft.trim());
    }

    public onClickThumbDown(): void {
        this._toggleEditor();
    }

    public onClickThumbUp(): void {
        this._toggleThumbUp();
    }

    /*----------------------------------------------------------------------------------------
     * Méthodes privées
     ----------------------------------------------------------------------------------------*/

    private _closeEditor(): void {
        this.editing = false;
        this.draft = '';
    }

    private _save(feedback: AgentTurnFeedback | null, feedbackComment: string): void {
        if (this.saving) return;
        this.saving = true;
        this._sessionService.feedbackTurn$(this.sessionId, this.turnIndex, feedback, feedbackComment).pipe(
            finalize(() => this.saving = false)
        ).subscribe(result => {
            if (!result) return;
            this._closeEditor();
            this.changed.emit(result);
        });
    }

    private _toggleEditor(): void {
        if (this.editing) {
            this._closeEditor();
            return;
        }
        this.draft = this.feedback === 'negative' ? this.feedbackComment : '';
        this.editing = true;
    }

    private _toggleThumbUp(): void {
        this._closeEditor();
        this._save(this.feedback === 'positive' ? null : 'positive', '');
    }
}
